import { Menu, MenuButton, MenuItem, MenuItems, type MenuItemsProps } from "@headlessui/react";
import { cva } from "class-variance-authority";
import {
	type ButtonHTMLAttributes,
	createContext,
	FC,
	type HTMLAttributes,
	type MouseEventHandler,
	type ReactNode,
	type Ref,
	useContext,
} from "react";
import { cn } from "../lib/utils";
import { trackingAttrs } from "../utils";
import { Button, type ButtonColor, type ButtonProps } from "./Button";
import { Icon } from "./Icon";

const MENU_CLASSES =
	"z-[200000] flex w-max flex-col gap-[2px] rounded-md border border-bc-default bg-surface-base p-[4px] shadow-md";

type ButtonDropdownCommonProps = Omit<HTMLAttributes<HTMLDivElement>, "color" | "onClick"> & {
	/** Content of the action half — the label of the button consumers click to act. */
	label: ReactNode;

	/**
	 * Fires when the action half is clicked. The trigger half always opens the menu.
	 * When this is omitted, the action half opens the menu too — use that when the label
	 * is the menu itself ("More actions") rather than its own command.
	 */
	onClick?: MouseEventHandler<HTMLButtonElement>;

	/** Optional icon rendered before `label`. Pair with the button `size` the same way as `Button`. */
	leadingIcon?: ReactNode;

	/** Disables both halves — the action can't be clicked and the menu can't be opened. */
	disabled?: boolean;

	/**
	 * Swaps the action label for a centered `Spinner` and blocks both halves, matching
	 * `Button`'s loading treatment.
	 * @default false
	 */
	loading?: boolean;

	/** Vertical size of both halves. Matches `Button`. @default "md" */
	size?: NonNullable<ButtonProps["size"]>;

	/** Fully rounds the outer corners of the joined control. Matches `Button`. @default false */
	pill?: boolean;

	/** `ButtonDropdown.Item` elements, rendered in the floating menu panel. */
	children?: ReactNode;

	/**
	 * When true, the menu picks which action the button half performs — `Item selected` tints
	 * the row and stamps a check. When omitted, the main button and each item are independent
	 * actions: the main half fires `onClick`, and each item fires its own.
	 * @default false
	 */
	changeSelected?: boolean;

	/**
	 * Headless floating placement. When omitted, the panel is portaled 6px under the control
	 * with the right edges aligned, so an `overflow-hidden` ancestor cannot clip it.
	 * Pass `false` to keep the panel in place under the control.
	 */
	anchor?: MenuItemsProps["anchor"];

	/** Accessible name for the chevron half, which has no visible label. @default "More options" */
	triggerAriaLabel?: string;

	/** Menu panel overrides — the panel sizes to its labels; pass a width to constrain it. */
	menuClassName?: string;

	/**
	 * Analytics identifier. Expands to `data-pendo-id` + `data-amp-track-id` on the root, with
	 * `-action` and `-trigger` suffixed variants on the two halves so each click is countable.
	 */
	trackingId?: string;

	/** Ref to the root element. */
	ref?: Ref<HTMLDivElement>;
};

/**
 * Chromed split buttons (`solid`/`outline`) require a semantic `color` — both halves are real
 * `Button`s painted by the same CVA matrix, so a `ButtonDropdown` and a `Button` with matching
 * props look identical except for the chevron seam.
 */
type ButtonDropdownChromeProps = {
	/**
	 * Visual treatment of both halves.
	 * - `solid`  — filled background
	 * - `outline` — transparent background with a colored border
	 * @default "solid"
	 */
	variant?: Exclude<NonNullable<ButtonProps["variant"]>, "ghost">;

	/** Semantic intent. Required for `solid`/`outline` — there is no sensible default. */
	color: ButtonColor;
};

type ButtonDropdownGhostProps = {
	variant: "ghost";

	/** Optional for ghost — color is supplied by the caller's `className`, not the variant matrix. */
	color?: ButtonColor;
};

type RootProps = ButtonDropdownCommonProps & (ButtonDropdownChromeProps | ButtonDropdownGhostProps);

const ButtonDropdownContext = createContext({ changeSelected: false });

/**
 * Seam between the two halves. Solid/ghost buttons have no border of their own, so the action
 * paints an explicit right edge; outline halves keep their borders and the trigger overlaps the
 * action by 1px (`-ml-px`) so the shared edge stays single-width.
 */
const seamClass = (variant: NonNullable<ButtonProps["variant"]>, color?: ButtonColor) => {
	if (variant === "outline") return undefined;
	if (variant === "solid" && color === "inverse") return "border-r border-bc-default";
	if (variant === "solid") return "border-r border-bc-inverse/[0.32]";
	return "border-r border-bc-default";
};

/**
 * ButtonDropdown primitive. A split button: an action half and a chevron half that opens a menu
 * of related actions. Both halves are real `Button`s — they take the same `variant` / `color` /
 * `size` / `pill` matrix — so the control can sit next to any `Button` and match it. Built on
 * Headless `Menu`. The panel is portaled, so it escapes ancestor `overflow: hidden`, and
 * arrow-key navigation, focus management, and click-outside dismissal come for free.
 *
 * Reach for `Button` when there is only one action, and for `SegmentedControls` when the
 * options are views to switch between rather than actions to run.
 */
const Root: FC<RootProps> = ({
	label,
	onClick,
	leadingIcon,
	disabled,
	loading,
	size = "md",
	pill = false,
	children,
	changeSelected = false,
	anchor = { to: "bottom end", gap: 6, padding: 8 },
	triggerAriaLabel = "More options",
	menuClassName,
	trackingId,
	className,
	variant: variantProp,
	color,
	ref,
	...rest
}) => {
	const variant = variantProp ?? "solid";
	const iconSize = size === "sm" ? "micro" : "mini";

	const buttonVariantProps =
		variant === "ghost"
			? ({ variant: "ghost", color } as const)
			: ({ variant, color: color as ButtonColor } as const);
	const actionButtonProps = {
		...buttonVariantProps,
		size,
		pill: false as const,
		className: cn(
			"grow rounded-r-none pr-3 font-medium",
			pill ? "rounded-l-full" : "rounded-l-[4px]",
			seamClass(variant, color),
		),
		leadingIcon,
		trackingId: trackingId ? `${trackingId}-action` : undefined,
		"data-testid": "button-dropdown-action",
	};

	return (
		<ButtonDropdownContext.Provider value={{ changeSelected }}>
			<Menu
				as="div"
				ref={ref}
				className={cn("relative inline-flex w-fit items-stretch", className)}
				{...trackingAttrs(trackingId)}
				{...rest}
			>
				{/*
				 * Chrome (fill, border, radius) lives on the two `Button` halves so hover/active
				 * light only the half under the pointer. `items-stretch` keeps the chevron the same
				 * height as the action regardless of `size`. `className` is on `Menu` (not an inner
				 * shell) so `w-full` stretches the real root instead of 100% of a `w-fit` parent.
				 */}
				{onClick ? (
					<Button {...actionButtonProps} onClick={onClick} disabled={disabled} loading={loading}>
						{label}
					</Button>
				) : (
					<MenuButton
						as={Button}
						{...actionButtonProps}
						disabled={disabled || loading}
						loading={loading}
					>
						{label}
					</MenuButton>
				)}
				<MenuButton
					as={Button}
					{...buttonVariantProps}
					size={size}
					pill={false}
					aria-label={triggerAriaLabel}
					disabled={disabled || loading}
					className={cn(
						"shrink-0 rounded-l-none px-2.5",
						pill ? "rounded-r-full" : "rounded-r-[4px]",
						variant === "outline" && "-ml-px",
					)}
					data-testid="button-dropdown-trigger"
					trackingId={trackingId ? `${trackingId}-trigger` : undefined}
				>
					<Icon type="chevron-down" size={iconSize} />
				</MenuButton>
				<MenuItems
					anchor={anchor || undefined}
					modal={false}
					className={cn(
						MENU_CLASSES,
						anchor === false && "absolute left-0 top-full mt-1.5",
						menuClassName,
					)}
					data-testid="button-dropdown-menu"
				>
					{children}
				</MenuItems>
			</Menu>
		</ButtonDropdownContext.Provider>
	);
};
Root.displayName = "ButtonDropdown";

const itemVariants = cva(
	"flex w-full cursor-pointer select-none items-center justify-between gap-[8px] whitespace-nowrap rounded-sm px-[10px] py-[8px] text-[13px] font-medium transition-colors data-[disabled]:cursor-not-allowed data-[disabled]:opacity-[0.45]",
	{
		variants: {
			// `data-focus` covers both hover and keyboard navigation, which Headless unifies.
			selected: {
				true: "bg-surface-accent text-brand-accent",
				false: "text-primary data-[focus]:bg-surface-neutral",
			},
		},
		defaultVariants: { selected: false },
	},
);

type ItemProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type"> & {
	/** Optional icon rendered before the label. Pair with `<Icon size="mini" />`. */
	leadingIcon?: ReactNode;

	/**
	 * Marks the item as the one currently in effect — tints the row and stamps a trailing
	 * check. Only takes effect when the root has `changeSelected`. Use it when the menu picks
	 * which action the button half performs.
	 * @default false
	 */
	selected?: boolean;

	/** Analytics identifier — expands to `data-pendo-id` + `data-amp-track-id`. */
	trackingId?: string;

	/** Ref to the element. */
	ref?: Ref<HTMLButtonElement>;
};

/**
 * A row in the menu panel. Renders a native `<button>` with `role="menuitem"`, so clicking it
 * fires `onClick` and closes the panel.
 */
const Item: FC<ItemProps> = ({
	leadingIcon,
	selected,
	trackingId,
	className,
	children,
	ref,
	...rest
}) => {
	const { changeSelected } = useContext(ButtonDropdownContext);
	const isSelected = changeSelected && selected;

	return (
		<MenuItem
			as="button"
			type="button"
			ref={ref}
			className={cn(itemVariants({ selected: isSelected }), className)}
			{...trackingAttrs(trackingId)}
			{...rest}
		>
			<span className="flex items-center gap-[8px]">
				{leadingIcon}
				{children}
			</span>
			{isSelected && (
				<>
					{/* Headless owns `role="menuitem"`, which can't carry `aria-checked` — so the check
					    reaches screen readers as part of the item's name instead. */}
					<span className="sr-only">Selected</span>
					<Icon type="check" size="mini" aria-hidden />
				</>
			)}
		</MenuItem>
	);
};
Item.displayName = "ButtonDropdown.Item";

export const ButtonDropdown = Object.assign(Root, { Item });

export type ButtonDropdownProps = RootProps;
export type ButtonDropdownItemProps = ItemProps;
